import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database, Tables } from '@/types/supabase';
import type { AdminListItem } from '@/lib/invite-helpers';

// Use Supabase types directly
export type Location = Tables<'location'>;
export type LocationAdmin = Tables<'location_admin'>;
export type Player = Tables<'player'>;
export type PlayerLocation = Tables<'player_location'>;
export type Federation = Tables<'federation'>;
export type FederationUser = Tables<'federation_user'>;

// Derived types using Supabase types
export type LocationListRow = Pick<
  Location,
  'id' | 'name' | 'address' | 'type' | 'federation_id' | 'status'
> & {
  admin_count: number;
  player_count: number;
  federation_name?: string | null;
};

export type LocationListOptions = {
  page: number;
  pageSize: number;
  sortKey?: 'name' | 'address' | 'type' | 'status' | 'federation_name';
  sortDir?: 'asc' | 'desc';
  type?: string;
  status?: string;
  search?: string;
};

export type LocationDetails = {
  location: Pick<
    Location,
    'id' | 'name' | 'address' | 'type' | 'phone' | 'email' | 'website' | 'federation_id' | 'status'
  > & {
    federation_name: string | null;
    description: string | null;
    logo: string | null;
  };
  admins: Array<{
    id: string;
    user_id: string;
    role: string;
    status: string;
    first_name: string | null;
    last_name: string | null;
    username: string | null;
  }>;
  players: Array<{
    id: string;
    first_name: string;
    last_name: string;
    current_rating: number;
    status: string;
    username: string;
    profile_photo: string | null;
  }>;
};

export type LocationUser = Pick<Player, 'user_id' | 'first_name' | 'last_name' | 'username'> & {
  is_already_admin: boolean;
};

export type LocationCreateData = Pick<
  Location,
  | 'name'
  | 'type'
  | 'address'
  | 'phone'
  | 'email'
  | 'website'
  | 'status'
  | 'description'
  | 'logo'
  | 'federation_id'
>;

export type LocationUpdateData = Partial<LocationCreateData>;

// Pure utility functions
const createCountMap = (items: Array<{ location_id: string }>): Map<string, number> => {
  const countMap = new Map<string, number>();
  items.forEach((item) => {
    const count = countMap.get(item.location_id) || 0;
    countMap.set(item.location_id, count + 1);
  });
  return countMap;
};

type LocationRowWithFederation = Pick<
  Location,
  'id' | 'name' | 'address' | 'type' | 'federation_id' | 'status'
> & { federation?: { name: string } | null };

const enrichLocationsWithCounts = (
  locations: LocationRowWithFederation[],
  adminCounts: Array<{ location_id: string }>,
  playerCounts: Array<{ location_id: string }>,
): LocationListRow[] => {
  const adminCountMap = createCountMap(adminCounts);
  const playerCountMap = createCountMap(playerCounts);

  return locations.map((location) => ({
    id: location.id,
    name: location.name,
    address: location.address,
    type: location.type,
    federation_id: location.federation_id,
    status: location.status,
    federation_name: location.federation?.name ?? null,
    admin_count: adminCountMap.get(location.id) || 0,
    player_count: playerCountMap.get(location.id) || 0,
  }));
};

export const locationServerModel = {
  // Names for known ids using the caller's (server) client
  async getNamesByIds(
    client: SupabaseClient<Database>,
    ids: string[],
  ): Promise<Array<Pick<Location, 'id' | 'name'>>> {
    if (ids.length === 0) return [];
    const { data, error } = await client.from('location').select('id, name').in('id', ids);
    if (error) throw error;
    return data ?? [];
  },

  // Permission checking
  async ensureLocationAccess(
    client: SupabaseClient<Database>,
    userId: string,
    locationId: string,
  ): Promise<{ isFederationAdmin: boolean; isLocationAdmin: boolean }> {
    // Get location's federation
    const { data: location, error: locationError } = await client
      .from('location')
      .select('federation_id')
      .eq('id', locationId)
      .single();

    if (locationError || !location?.federation_id) {
      throw Object.assign(new Error('Location not found'), { status: 404 });
    }

    // Check federation admin status
    const { data: fedAdmin, error: fedError } = await client
      .from('federation_user')
      .select('id')
      .eq('federation_id', location.federation_id)
      .eq('user_id', userId)
      .eq('role', 'admin')
      .eq('status', 'active')
      .maybeSingle();

    if (fedError) throw fedError;

    // Check location admin status
    const { data: locAdmin, error: locAdminError } = await client
      .from('location_admin')
      .select('id')
      .eq('location_id', locationId)
      .eq('user_id', userId)
      .eq('status', 'active')
      .maybeSingle();

    if (locAdminError) throw locAdminError;

    const isFederationAdmin = Boolean(fedAdmin);
    const isLocationAdmin = Boolean(locAdmin);

    if (!isFederationAdmin && !isLocationAdmin) {
      throw Object.assign(new Error('Forbidden'), { status: 403 });
    }

    return { isFederationAdmin, isLocationAdmin };
  },

  // Basic CRUD operations
  async get(client: SupabaseClient<Database>, locationId: string): Promise<Location> {
    const { data: location, error } = await client
      .from('location')
      .select('*')
      .eq('id', locationId)
      .single();

    if (error) throw error;
    return location;
  },

  // Get location with admins for logging purposes
  async getLocationWithAdmins(
    client: SupabaseClient<Database>,
    locationId: string,
  ): Promise<Location | null> {
    const { data: location, error } = await client
      .from('location')
      .select('*')
      .eq('id', locationId)
      .single();

    if (error) throw error;
    return location;
  },

  async update(
    client: SupabaseClient<Database>,
    locationId: string,
    updates: LocationUpdateData,
  ): Promise<void> {
    const { error } = await client.from('location').update(updates).eq('id', locationId);

    if (error) throw error;
  },

  // Legacy method for backward compatibility
  async updateLocation(
    client: SupabaseClient<Database>,
    locationId: string,
    updates: LocationUpdateData,
  ): Promise<void> {
    return this.update(client, locationId, updates);
  },

  async create(
    client: SupabaseClient<Database>,
    userId: string,
    locationData: LocationCreateData,
  ): Promise<string> {
    // Create the location with the provided federation_id
    const { data: location, error } = await client
      .from('location')
      .insert({
        ...locationData,
      })
      .select('id')
      .single();

    if (error) throw error;
    return location.id;
  },

  // Legacy method for backward compatibility
  async createLocation(
    client: SupabaseClient<Database>,
    userId: string,
    locationData: LocationCreateData,
  ): Promise<{ success: boolean; locationId?: string; error?: string }> {
    try {
      const locationId = await this.create(client, userId, locationData);
      return { success: true, locationId };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Internal server error';
      return { success: false, error: errorMessage };
    }
  },

  // List operations
  async listByFederationId(
    client: SupabaseClient<Database>,
    federationId: string,
  ): Promise<LocationListRow[]> {
    // Get locations
    const { data: locations, error: locationError } = await client
      .from('location')
      .select('id, name, address, type, federation_id, status')
      .eq('status', 'active')
      .eq('federation_id', federationId)
      .order('name');

    if (locationError) throw locationError;
    if (!locations?.length) return [];

    // Get counts in parallel.
    // Filter through the location FK rather than `.in('location_id', locationIds)`: a
    // federation can hold hundreds of locations, and a few hundred UUIDs in a GET query
    // string overflows PostgREST's URL limit (HTTP 414 "URI too long").
    const [adminCounts, playerCounts] = await Promise.all([
      client
        .from('location_admin')
        .select('location_id, location!inner(federation_id, status)')
        .eq('location.federation_id', federationId)
        .eq('location.status', 'active')
        .eq('status', 'active'),
      client
        .from('player_location')
        .select('location_id, location!inner(federation_id, status)')
        .eq('location.federation_id', federationId)
        .eq('location.status', 'active')
        .eq('status', 'active'),
    ]);

    if (adminCounts.error) throw adminCounts.error;
    if (playerCounts.error) throw playerCounts.error;

    return enrichLocationsWithCounts(locations, adminCounts.data || [], playerCounts.data || []);
  },

  async listByFederationIds(
    client: SupabaseClient<Database>,
    federationIds: string[],
  ): Promise<LocationListRow[]> {
    if (!federationIds.length) return [];

    const { data: locations, error: locationError } = await client
      .from('location')
      .select('id, name, address, type, federation_id, status')
      .eq('status', 'active')
      .in('federation_id', federationIds)
      .order('name');

    if (locationError) throw locationError;
    if (!locations?.length) return [];

    // Get counts in parallel — filtered through the location FK, not a locationIds list
    // (see listByFederationId: hundreds of UUIDs in the query string trips HTTP 414).
    const [adminCounts, playerCounts] = await Promise.all([
      client
        .from('location_admin')
        .select('location_id, location!inner(federation_id, status)')
        .in('location.federation_id', federationIds)
        .eq('location.status', 'active')
        .eq('status', 'active'),
      client
        .from('player_location')
        .select('location_id, location!inner(federation_id, status)')
        .in('location.federation_id', federationIds)
        .eq('location.status', 'active')
        .eq('status', 'active'),
    ]);

    if (adminCounts.error) throw adminCounts.error;
    if (playerCounts.error) throw playerCounts.error;

    return enrichLocationsWithCounts(locations, adminCounts.data || [], playerCounts.data || []);
  },

  async listByIds(
    client: SupabaseClient<Database>,
    locationIds: string[],
  ): Promise<LocationListRow[]> {
    if (!locationIds.length) return [];

    const { data: locations, error: locationError } = await client
      .from('location')
      .select('id, name, address, type, federation_id, status')
      .eq('status', 'active')
      .in('id', locationIds)
      .order('name');

    if (locationError) throw locationError;
    if (!locations?.length) return [];

    // Get counts in parallel
    const [adminCounts, playerCounts] = await Promise.all([
      client
        .from('location_admin')
        .select('location_id')
        .in('location_id', locationIds)
        .eq('status', 'active'),
      client
        .from('player_location')
        .select('location_id')
        .in('location_id', locationIds)
        .eq('status', 'active'),
    ]);

    if (adminCounts.error) throw adminCounts.error;
    if (playerCounts.error) throw playerCounts.error;

    return enrichLocationsWithCounts(locations, adminCounts.data || [], playerCounts.data || []);
  },

  /**
   * List locations by federation IDs with server-side pagination, filter, and sort.
   */
  async listByFederationIdsPaginated(
    client: SupabaseClient<Database>,
    federationIds: string[],
    options: LocationListOptions,
  ): Promise<{ locations: LocationListRow[]; total: number }> {
    if (!federationIds.length) return { locations: [], total: 0 };

    const { page, pageSize, sortKey = 'name', sortDir = 'asc', type, status, search } = options;
    const offset = (page - 1) * pageSize;
    const ascending = sortDir !== 'desc';

    let query = client
      .from('location')
      .select('id, name, address, type, federation_id, status, federation:federation_id(name)', {
        count: 'exact',
      })
      .in('federation_id', federationIds);

    if (type) query = query.eq('type', type);
    if (status) query = query.eq('status', status);
    if (search && search.trim()) {
      const q = search.trim();
      query = query.or(`name.ilike.%${q}%,address.ilike.%${q}%`);
    }

    const orderKey = sortKey === 'federation_name' ? 'name' : sortKey;
    const {
      data: locations,
      error: locationError,
      count,
    } = await query.order(orderKey, { ascending }).range(offset, offset + pageSize - 1);

    if (locationError) throw locationError;
    const rows = (locations || []) as LocationRowWithFederation[];
    if (!rows.length) return { locations: [], total: count ?? 0 };

    const locationIds = rows.map((l) => l.id);
    const [adminCounts, playerCounts] = await Promise.all([
      client
        .from('location_admin')
        .select('location_id')
        .in('location_id', locationIds)
        .eq('status', 'active'),
      client
        .from('player_location')
        .select('location_id')
        .in('location_id', locationIds)
        .eq('status', 'active'),
    ]);

    if (adminCounts.error) throw adminCounts.error;
    if (playerCounts.error) throw playerCounts.error;

    const listRows = enrichLocationsWithCounts(
      rows,
      adminCounts.data || [],
      playerCounts.data || [],
    );
    if (sortKey === 'federation_name') {
      listRows.sort((a, b) => {
        const aVal = a.federation_name ?? '';
        const bVal = b.federation_name ?? '';
        return ascending ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      });
    }
    return { locations: listRows, total: count ?? 0 };
  },

  /**
   * List locations by location IDs with server-side pagination, filter, and sort.
   */
  async listByIdsPaginated(
    client: SupabaseClient<Database>,
    locationIds: string[],
    options: LocationListOptions,
  ): Promise<{ locations: LocationListRow[]; total: number }> {
    if (!locationIds.length) return { locations: [], total: 0 };

    const { page, pageSize, sortKey = 'name', sortDir = 'asc', type, status, search } = options;
    const offset = (page - 1) * pageSize;
    const ascending = sortDir !== 'desc';

    let query = client
      .from('location')
      .select('id, name, address, type, federation_id, status, federation:federation_id(name)', {
        count: 'exact',
      })
      .in('id', locationIds);

    if (type) query = query.eq('type', type);
    if (status) query = query.eq('status', status);
    if (search && search.trim()) {
      const q = search.trim();
      query = query.or(`name.ilike.%${q}%,address.ilike.%${q}%`);
    }

    const orderKey = sortKey === 'federation_name' ? 'name' : sortKey;
    const {
      data: locations,
      error: locationError,
      count,
    } = await query.order(orderKey, { ascending }).range(offset, offset + pageSize - 1);

    if (locationError) throw locationError;
    const rows = (locations || []) as LocationRowWithFederation[];
    if (!rows.length) return { locations: [], total: count ?? 0 };

    const ids = rows.map((l) => l.id);
    const [adminCounts, playerCounts] = await Promise.all([
      client
        .from('location_admin')
        .select('location_id')
        .in('location_id', ids)
        .eq('status', 'active'),
      client
        .from('player_location')
        .select('location_id')
        .in('location_id', ids)
        .eq('status', 'active'),
    ]);

    if (adminCounts.error) throw adminCounts.error;
    if (playerCounts.error) throw playerCounts.error;

    const listRows = enrichLocationsWithCounts(
      rows,
      adminCounts.data || [],
      playerCounts.data || [],
    );
    if (sortKey === 'federation_name') {
      listRows.sort((a, b) => {
        const aVal = a.federation_name ?? '';
        const bVal = b.federation_name ?? '';
        return ascending ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      });
    }
    return { locations: listRows, total: count ?? 0 };
  },

  // Admin management
  async getLocationAdmins(client: SupabaseClient<Database>, locationId: string) {
    const { resolveAdminDisplayInfo } = await import('@/lib/invite-helpers');

    const { data: admins, error: adminError } = await client
      .from('location_admin')
      .select('id, user_id, role, status')
      .eq('location_id', locationId)
      .eq('status', 'active');

    if (adminError) throw adminError;
    if (!admins?.length) return [];

    const userIds = admins.map((admin) => admin.user_id).filter(Boolean);
    return resolveAdminDisplayInfo(client, userIds);
  },

  async getLocationPlayers(
    client: SupabaseClient<Database>,
    locationId: string,
  ): Promise<Player[]> {
    const { data: playerLocations, error } = await client
      .from('player_location')
      .select(
        'player(id, user_id, first_name, last_name, username, current_rating, profile_photo, gender)',
      )
      .eq('location_id', locationId)
      .eq('status', 'active');

    if (error) throw error;
    const players = (playerLocations || []).map((p) => p.player as Player);

    // Sort by rating descending (highest first)
    return players.sort((a, b) => {
      const ratingA = a.current_rating || 0;
      const ratingB = b.current_rating || 0;
      return ratingB - ratingA;
    });
  },

  // User search
  async searchUsersForLocation(
    client: SupabaseClient<Database>,
    locationId: string,
    search: string,
    limit: number,
  ): Promise<LocationUser[]> {
    // Build search query
    let query = client.from('player').select('user_id, first_name, last_name, username');

    if (search) {
      const searchLower = search.toLowerCase();
      query = query.or(
        `first_name.ilike.%${searchLower}%,last_name.ilike.%${searchLower}%,username.ilike.%${searchLower}%`,
      );
    }

    const { data: users, error: userError } = await query.limit(limit);
    if (userError) throw userError;
    if (!users?.length) return [];

    // Get existing admins
    const { data: existingAdmins, error: adminError } = await client
      .from('location_admin')
      .select('user_id')
      .eq('location_id', locationId)
      .eq('status', 'active');

    if (adminError) throw adminError;

    const existingAdminIds = new Set((existingAdmins || []).map((a) => a.user_id));

    return users
      .filter((user): user is typeof user & { user_id: string } => Boolean(user.user_id))
      .map((user) => ({
        user_id: user.user_id,
        first_name: user.first_name,
        last_name: user.last_name,
        username: user.username || '',
        is_already_admin: existingAdminIds.has(user.user_id),
      }));
  },

  // Admin operations
  async addLocationAdmin(
    client: SupabaseClient<Database>,
    locationId: string,
    userId: string,
    role: string,
  ): Promise<void> {
    // Check if already exists (no unique constraint on location_id + user_id)
    const { data: existing } = await client
      .from('location_admin')
      .select('id, status')
      .eq('location_id', locationId)
      .eq('user_id', userId)
      .maybeSingle();

    if (existing && existing.status === 'active') {
      throw Object.assign(new Error('Admin already exists'), { status: 409 });
    }

    if (existing) {
      // Re-activate inactive admin
      const { error } = await client
        .from('location_admin')
        .update({ role, status: 'active' })
        .eq('id', existing.id);
      if (error) throw error;
    } else {
      const { error } = await client.from('location_admin').insert({
        location_id: locationId,
        user_id: userId,
        role,
        status: 'active',
      });
      if (error) throw error;
    }
  },

  async removeLocationAdmin(
    client: SupabaseClient<Database>,
    locationId: string,
    adminId: string,
  ): Promise<void> {
    const { error } = await client
      .from('location_admin')
      .delete()
      .eq('user_id', adminId)
      .eq('location_id', locationId);

    if (error) throw error;
  },

  async updateLocationAdminRole(
    client: SupabaseClient<Database>,
    locationId: string,
    userId: string,
    role: string,
  ): Promise<void> {
    // Keyed by user_id for consistency with add/removeLocationAdmin (was the row
    // id; this method had no callers). Only touches the active row.
    const { error } = await client
      .from('location_admin')
      .update({ role })
      .eq('user_id', userId)
      .eq('location_id', locationId)
      .eq('status', 'active');

    if (error) throw error;
  },

  /**
   * Club staff for the Settings → Users tab: active location_admins with their
   * role + display info. (getLocationAdmins drops the role via
   * resolveAdminDisplayInfo; this preserves it.)
   */
  async listClubStaff(
    client: SupabaseClient<Database>,
    locationId: string,
  ): Promise<Array<AdminListItem & { role: string }>> {
    const { data: rows, error } = await client
      .from('location_admin')
      .select('user_id, role')
      .eq('location_id', locationId)
      .eq('status', 'active');
    if (error) throw error;
    if (!rows?.length) return [];

    const { resolveAdminDisplayInfo } = await import('@/lib/invite-helpers');
    const userIds = rows.map((r) => r.user_id).filter(Boolean) as string[];
    const display = await resolveAdminDisplayInfo(client, userIds);
    const roleByUser = new Map(rows.map((r) => [r.user_id, r.role] as const));
    return display.map((d) => ({ ...d, role: roleByUser.get(d.user_id) ?? 'viewer' }));
  },

  // Player operations
  async addLocationPlayer(
    client: SupabaseClient<Database>,
    locationId: string,
    playerId: string,
  ): Promise<void> {
    // Idempotent on (player_id, location_id) — re-adding an already-linked
    // player must update, not insert a duplicate (the unique constraint is on
    // those columns, not the PK, so a bare upsert would violate it).
    const { error } = await client.from('player_location').upsert(
      {
        location_id: locationId,
        player_id: playerId,
        status: 'active',
      },
      { onConflict: 'player_id,location_id' },
    );

    if (error) throw error;
  },

  async removeLocationPlayer(
    client: SupabaseClient<Database>,
    locationId: string,
    playerId: string,
  ): Promise<void> {
    const { error } = await client
      .from('player_location')
      .delete()
      .eq('player_id', playerId)
      .eq('location_id', locationId);

    if (error) throw error;
  },

  async listLocationPlayersPaginated(
    client: SupabaseClient<Database>,
    locationId: string,
    options: {
      page: number;
      pageSize: number;
      sortBy: 'name' | 'email' | 'gender' | 'rating' | 'created_at';
      sortDir: 'asc' | 'desc';
      search?: string;
      gender?: string;
      ratingMin?: number;
      ratingMax?: number;
      /** Inclusive ISO bounds on COALESCE(joined_at, created_at). */
      joinedAfter?: string;
      joinedBefore?: string;
    },
  ): Promise<{
    rows: Array<{
      player_id: string;
      user_id: string | null;
      first_name: string | null;
      last_name: string | null;
      alias: string | null;
      gender: string | null;
      email: string | null;
      rating: number | null;
      rating_delta: number | null;
      username: string | null;
      created_at: string | null;
    }>;
    total: number;
  }> {
    const {
      page,
      pageSize,
      sortBy,
      sortDir,
      search,
      gender,
      ratingMin,
      ratingMax,
      joinedAfter,
      joinedBefore,
    } = options;

    type LocationPlayerRow = {
      player_id: string;
      user_id: string | null;
      first_name: string | null;
      last_name: string | null;
      alias: string | null;
      gender: string | null;
      email: string | null;
      current_rating: number | null;
      rating_delta: number | null;
      username: string | null;
      created_at: string | null;
      total_count: number;
    };

    // Cast needed: RPC not yet in generated types (run npm run types:gen after migration)
    const { data, error } = (await (client.rpc as Function)('search_location_players', {
      p_location_id: locationId,
      search_term: search || '',
      gender_filter: gender || 'all',
      sort_by: sortBy,
      sort_dir: sortDir,
      page_offset: (page - 1) * pageSize,
      page_limit: pageSize,
      rating_min: ratingMin ?? undefined,
      rating_max: ratingMax ?? undefined,
      joined_after: joinedAfter ?? undefined,
      joined_before: joinedBefore ?? undefined,
    })) as { data: LocationPlayerRow[] | null; error: { message: string } | null };

    if (error) {
      console.error('Error searching location players:', error);
      throw error;
    }

    const rows = (data || []).map((row) => ({
      player_id: row.player_id,
      user_id: row.user_id,
      first_name: row.first_name,
      last_name: row.last_name,
      alias: row.alias,
      gender: row.gender,
      email: row.email,
      rating: row.current_rating,
      rating_delta: row.rating_delta,
      username: row.username,
      created_at: row.created_at,
    }));

    const total = data && data.length > 0 ? data[0].total_count : 0;

    return { rows, total };
  },

  async batchUpdateNames(
    client: SupabaseClient<Database>,
    updates: Array<{ id: string; name: string }>,
  ): Promise<{
    applied: number;
    failed: number;
    errors: Array<{ id: string; name: string; error: string }>;
  }> {
    let applied = 0;
    let failed = 0;
    const errors: Array<{ id: string; name: string; error: string }> = [];

    for (const update of updates) {
      try {
        const { error } = await client
          .from('location')
          .update({ name: update.name })
          .eq('id', update.id);

        if (error) throw error;
        applied++;
      } catch (err) {
        failed++;
        errors.push({
          id: update.id,
          name: update.name,
          error: err instanceof Error ? err.message : 'Unknown error',
        });
      }
    }

    return { applied, failed, errors };
  },

  // ── Club portal authorisation ──────────────────────────────────────────────
  // Thin reads consumed by src/app/[locale]/club/libs/resolveClubAccess.ts.

  /** Resolve a club's location by its URL slug. Null when no such slug. */
  async getLocationBySlug(
    client: SupabaseClient<Database>,
    slug: string,
  ): Promise<Pick<Location, 'id' | 'slug' | 'federation_id'> | null> {
    const { data, error } = await client
      .from('location')
      .select('id, slug, federation_id')
      .eq('slug', slug)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  /** The user's active location_admin role at a location, or null. */
  async getLocationAdminRole(
    client: SupabaseClient<Database>,
    userId: string,
    locationId: string,
  ): Promise<string | null> {
    const { data, error } = await client
      .from('location_admin')
      .select('role')
      .eq('user_id', userId)
      .eq('location_id', locationId)
      .eq('status', 'active')
      .maybeSingle();
    if (error) throw error;
    return data?.role ?? null;
  },

  /**
   * The user's active location_coach role at a location, or null. Resolves the
   * user's player internally (coaches are players) so callers pass a userId.
   */
  async getLocationCoachRole(
    client: SupabaseClient<Database>,
    userId: string,
    locationId: string,
  ): Promise<string | null> {
    const { data: player } = await client
      .from('player')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();
    if (!player?.id) return null;

    const { data, error } = await client
      .from('location_coach')
      .select('role')
      .eq('player_id', player.id)
      .eq('location_id', locationId)
      .eq('status', 'active')
      .maybeSingle();
    if (error) throw error;
    return data?.role ?? null;
  },

  /**
   * The clubs shown in the portal switcher: the locations the user is a direct,
   * active `location_admin` of. (Federation-admin / super-admin can still reach
   * other clubs by URL via resolveClubAccess, but they are NOT listed here — the
   * switcher is "the clubs I run", not "every club I could enter".) Ordered by
   * name; null-slug locations are excluded defensively (the slug invariant makes
   * that a no-op post-backfill). Whether each club's portal opens is the
   * club-module resolver's answer (`listClubsWithPortal` in club/libs/resolveClubAccess),
   * so the landing page can tell "not live yet" from "no access".
   */
  async listAccessibleClubs(
    client: SupabaseClient<Database>,
    userId: string,
  ): Promise<Array<{ locationId: string; slug: string; name: string }>> {
    const { data: adminRows, error: adminErr } = await client
      .from('location_admin')
      .select('location_id')
      .eq('user_id', userId)
      .eq('status', 'active');
    if (adminErr) throw adminErr;

    const locationIds = [...new Set((adminRows ?? []).map((r) => r.location_id))];
    if (locationIds.length === 0) return [];

    const { data: locs, error: locErr } = await client
      .from('location')
      .select('id, slug, name')
      .in('id', locationIds)
      .not('slug', 'is', null)
      .order('name');
    if (locErr) throw locErr;
    return (locs ?? []).map((l) => ({
      locationId: l.id,
      slug: l.slug as string,
      name: l.name,
    }));
  },

  async updateLocationSettings(
    client: SupabaseClient<Database>,
    locationId: string,
    settings: Database['public']['Tables']['location']['Row']['settings'],
  ): Promise<void> {
    const { error } = await client.from('location').update({ settings }).eq('id', locationId);
    if (error) throw new Error(`updateLocationSettings failed: ${error.message}`);
  },

  /**
   * LD-2: atomic, key-scoped merge into `location.settings` via the
   * `location_merge_settings` RPC (migration 20260702124000) — replaces the old
   * read-in-JS / patch / write-the-whole-blob pattern, which lost concurrent writers'
   * keys when two requests interleaved (e.g. onboarding-dismiss racing a settings save).
   * Pass only the changed keys; every other key in the stored blob is left untouched.
   */
  async mergeLocationSettings(
    client: SupabaseClient<Database>,
    locationId: string,
    patch: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    const { data, error } = await client.rpc('location_merge_settings', {
      p_location_id: locationId,
      p_patch: patch as never,
    });
    if (error) throw new Error(`mergeLocationSettings failed: ${error.message}`);
    return (data ?? {}) as Record<string, unknown>;
  },

  /**
   * Soft-remove a player from a club's roster: archive the link instead of
   * deleting it. The list RPC (`search_location_players`) only returns
   * `status = 'active'` rows, so an archived player drops off the roster
   * immediately while their staff note + joined date are preserved. Re-adding
   * via {@link addLocationPlayer} upserts `status = 'active'`, cleanly
   * un-archiving the same row (reversible, no duplicate). Membership itself is
   * derived (`location_member_active`), so archiving touches only the status.
   */
  async archiveLocationPlayer(
    client: SupabaseClient<Database>,
    locationId: string,
    playerId: string,
  ): Promise<void> {
    const { error } = await client
      .from('player_location')
      .update({ status: 'archived' })
      .eq('player_id', playerId)
      .eq('location_id', locationId);
    if (error) throw new Error(`archiveLocationPlayer failed: ${error.message}`);
  },

  /** The location's name, or null when the id matches no row. */
  async getNameById(client: SupabaseClient<Database>, locationId: string): Promise<string | null> {
    const { data, error } = await client
      .from('location')
      .select('name')
      .eq('id', locationId)
      .maybeSingle();
    if (error) throw new Error(`getNameById failed: ${error.message}`);
    return data?.name ?? null;
  },
};
